import { SetOptional } from 'type-fest';
import { Campaign, CampaignWebsite } from './Campaign';
import { User } from './UserProfile';

export enum ApiRoutes {
  Metadata = '/metadata',
  Stats = '/stats',
  CampaignStats = '/campaigns/stats',
  Feedback = '/feedback',
  ReportingUrls = '/stats/reporting-urls',
  StatsTotalProfiles = '/stats/total-profiles',
  Login = '/auth/login',
  Logout = '/auth/logout',
  UsersList = '/users',
  UserProfile = '/users/me',
  SetUserGa4PropertyId = '/users/me/ga4-property-id',
  CheckUserGa4PropertyId = '/users/me/check-ga4-connection',
  ResetPassword = '/auth/reset-password',
  Campaigns = '/campaigns',
  AllCampaigns = '/campaigns/all',
  Campaign = '/campaigns/:campaignId',
  DuplicateCampaign = '/campaigns/:campaignId/duplicate',
  PublishedCampaign = '/campaigns/:campaignId/published',
  PublishCampaign = '/campaigns/:campaignId/publish',
  StopCampaign = '/campaigns/:campaignId/stop',
  CampaignTargetingSuggestions = '/campaigns/:campaignId/targeting-suggestions',
  GetCreativeTypes = '/creative-types',
  AddCampaignCreative = '/campaigns/:campaignId/creatives',
  DeleteCreative = '/campaigns/:campaignId/creatives/:creativeId',
  GetCampaignUrl = '/campaigns/:campaignId/ga4-config-url',
  GetGa4Info = '/campaigns/:campaignId/ga4-info',
  SetGa4PropertyId = '/campaigns/:campaignId/ga4-property-id',
  CheckGa4Connection = '/campaigns/:campaignId/check-ga4-connection',
  AddCampaignTargeting = '/campaigns/:campaignId/targetings',
  TargetGroupSizeEstimation = '/campaigns/:campaignId/targetings/:targetingId/estimate-size',
  UpdateCampaignTargeting = '/campaigns/:campaignId/targetings/:targetingId',
  DeleteCampaignTargeting = '/campaigns/:campaignId/targetings/:targetingId',
  GetCampaignTargetingGeoAutocomplete = '/campaigns/targetings/geo-autocomplete',
  Invoices = '/invoices',
  InvoicesAll = '/invoices/all',
  GenerateInvoice = '/invoices/generate',
  MarkInvoiceAsPaid = '/invoices/:invoiceId/mark-as-paid',
  Impersonate = '/auth/impersonate',
  ImpersonateReset = '/auth/impersonate-reset',
  ForceEmailSend = '/force-email-send',
}

export type MetadataApiResponse = {
  user: User;
  impersonator?: User;
  loginCount: number;
  isGeneralFeedbackSubmitted: boolean;
};

export type CampaignApiResponse = {
  campaign: Campaign;
};

export type CampaignWithLowPerformance = Campaign & {
  hasLowPerformance: boolean;
};

export type CampaignListApiResponse = {
  campaigns: CampaignWithLowPerformance[];
};

export type WebsiteRequestItem = SetOptional<CampaignWebsite, 'id'>;

export type CreateCampaignRequest = Pick<Campaign, 'name' | 'budget'> & {
  startDate: string;
  endDate: string;
  websites: { url: string }[];
};

export type UpdateCampaignRequest = Omit<
  Campaign,
  | 'id'
  | 'state'
  | 'ga4ConnectionValid'
  | 'ga4ConnectionCheckedAt'
  | 'startDate'
  | 'endDate'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'cockpitId'
  | 'creatives'
  | 'websites'
  | 'effectiveCostPerUnit'
  | 'invoicedAmount'
  | 'paidAmount'
  | 'stoppedAt'
> & {
  startDate: string;
  endDate: string;
  websites: WebsiteRequestItem[];
};

export type UpdatePublishedCampaignRequest = Partial<Pick<Campaign, 'budget' | 'maxUnitPrice'>>;

export type AddCampaignCreativeRequest = {
  file: File;
  creativeTypeId: string;
  websiteId: number;
  campaignId: number;
};

export type DefaultSuccessCampaignResponse = {
  id: number;
};

export type Ga4CampaignUrlApiResponse = {
  url: string;
};

export type Ga4CampaignInfoApiResponse = {
  ga4PropertyId: string;
  ga4ConnectionValid: boolean;
  ga4ConnectionCheckedAt: Date | null;
  createdBy: {
    email: string;
    firstName: string;
    lastName: string;
  };
};

export type Ga4ConnectionResponse = {
  id: number;
  successful: boolean;
};

export type ForceEmailSendRequest = {
  emailType: string;
  campaignId?: number;
};

export type ForceEmailSendResponse = {
  success: boolean;
  message: string;
};
